import { type TypedDocumentNode, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { notify } from 'src/lib/notify';
import { useEditStateMutation } from 'src/providers/EditingStateProvider';
import type {
  EditPricingSheets,
  EditPricingSheetsVariables,
  UpdatePricingSheet,
  UpdatePricingSheetInput,
  UpdatePricingSheetVariables,
} from 'types/graphql';

import { PRICING_SHEET_CORE_QUERY } from './usePricingSheet';
import { PRICING_SHEETS_PAGE_QUERY } from './usePricingSheetsPage';

/**
 * One root (`updatePricingSheet`), two documents. The operation names are telemetry identity, so the editor
 * keeps `EditPricingSheets` and the list keeps `UpdatePricingSheet`; each hook below owns one behavior.
 */
export const EDIT_PRICING_SHEET: TypedDocumentNode<EditPricingSheets, EditPricingSheetsVariables> = gql`
  mutation EditPricingSheets($id: Int!, $input: UpdatePricingSheetInput!) {
    updatePricingSheet(id: $id, input: $input) {
      id
    }
  }
`;

export const UPDATE_PRICING_SHEET: TypedDocumentNode<UpdatePricingSheet, UpdatePricingSheetVariables> = gql`
  mutation UpdatePricingSheet($id: Int!, $input: UpdatePricingSheetInput!) {
    updatePricingSheet(id: $id, input: $input) {
      id
    }
  }
`;

type UpdateArgs = { id: number; input: UpdatePricingSheetInput };

/** The list's update door (e.g. the Enabled switch). */
export function useUpdatePricingSheet() {
  const [mutate, { loading, error }] = useMutation(UPDATE_PRICING_SHEET, {
    refetchQueries: [PRICING_SHEETS_PAGE_QUERY],
  });

  const update = useCallback(({ id, input }: UpdateArgs) => mutate({ variables: { id, input } }), [mutate]);

  return { update, loading, error };
}

/** The settings panel's debounced autosave: held in the 'edit' barrier, re-reads the sheet core, toasts on failure. */
export function useSavePricingSheetSettings() {
  const [mutate, { loading, error }] = useEditStateMutation<EditPricingSheets, EditPricingSheetsVariables>(
    'edit',
    EDIT_PRICING_SHEET,
    { refetchQueries: [PRICING_SHEET_CORE_QUERY], onError: () => notify.error('Failed to save pricing sheet') }
  );

  const save = useCallback(({ id, input }: UpdateArgs) => mutate({ variables: { id, input } }), [mutate]);

  return { save, loading, error };
}

/** The setup stepper's final step: the draft goes live. The caller owns the toasts and navigation. */
export function usePublishPricingSheet() {
  const [mutate, { loading, error }] = useMutation(EDIT_PRICING_SHEET);

  const publish = useCallback(
    (id: number) => mutate({ variables: { id, input: { draft: false, enabled: true } } }),
    [mutate]
  );

  return { publish, loading, error };
}
